// BUILD-046 correction cycle 1bb88b3e (stage B1, item F1 on the screen): completed-task DISCOVERY and Reopen in Team Work.
//
// The Team Work (oversight) list shows OPEN tasks and unclaimed shared tasks. A manager or the designated company holder who needs to find a task that was
// completed (to reopen it) switches the list filter to Completed. The filter is a view of the SAME scope the open list uses: a manager's Direct_Reports,
// the company for the designated Owner (the server decides; the client sends no scope). It is read page by page (`GET /work/oversight?status=completed`,
// newest first) and never replaces the open list: the default filter is Open, and switching back shows the open list again.
//
// Reopen of a completed row is the existing action (`POST /work/{id}/restore`) behind the existing confirmation (TaskActionControls: Reopen, then one
// confirmation). It is offered to an Authorized_Overseer on an ORDINARY completed task. A source-governed completed task is not reopened here (the server
// refuses it atomically with SOURCE_GOVERNED): its row shows the explanation instead of a working Reopen. After an accepted Reopen the host reads the
// completed list again (`load`), so the row leaves it and a reload shows the same: the server is the truth, nothing is removed by guessing.
//
// An older server that ignores the filter answers the OPEN list without `filter`; the client refuses that (api/workShared.ts FILTER_NOT_APPLIED), so this
// list says "not available on this server yet" and never presents open tasks as completed.
//
// Pure module apart from the paged engine it builds. Runtime imports: the action model, the paged model and the paged engine. Erasable TypeScript only.

import { isOverseer, offeredControls } from './oversightActionsModel';
import type { ActionMe, ActionRow } from './oversightActionsModel';
import { createPagedList } from './pagedListController';
import type { PagedList, PagedListDeps } from './pagedListController';
import { describePaged } from './pagedListModel';
import type { PagedState, PagedView } from './pagedListModel';

// ───────────────────────── the filter ─────────────────────────

export type OversightFilter = 'open' | 'completed';

/** The list opens on the open tasks; Completed is chosen on purpose. */
export const DEFAULT_OVERSIGHT_FILTER: OversightFilter = 'open';

export const OVERSIGHT_FILTER_OPTIONS: readonly { value: OversightFilter; label: string }[] = Object.freeze([
  { value: 'open' as OversightFilter, label: 'All open' },
  { value: 'completed' as OversightFilter, label: 'Completed' },
]);

/** Entries per page of the completed list (the server's own default is the same). */
export const COMPLETED_PAGE_SIZE = 50;

export function isOversightFilter(value: unknown): value is OversightFilter {
  return value === 'open' || value === 'completed';
}

export interface OversightFilterView {
  filter: OversightFilter;
  options: { value: OversightFilter; label: string; selected: boolean }[];
  /** The heading of the list under this filter. */
  listLabel: string;
  /** True when the rows come from the paged completed read (and the host should ask for it when the filter is chosen). */
  paged: boolean;
}

export function describeOversightFilter(filter: OversightFilter): OversightFilterView {
  const chosen: OversightFilter = isOversightFilter(filter) ? filter : DEFAULT_OVERSIGHT_FILTER;
  return {
    filter: chosen,
    options: OVERSIGHT_FILTER_OPTIONS.map((o) => ({ ...o, selected: o.value === chosen })),
    listLabel: chosen === 'completed' ? 'completed team work' : 'team work',
    paged: chosen === 'completed',
  };
}

// ───────────────────────── Reopen on a completed row ─────────────────────────

export const GOVERNED_REOPEN_TEXT = 'This task completes through its source, so it cannot be reopened here.';

export type ReopenOffer = { kind: 'reopen' } | { kind: 'governed'; text: string } | { kind: 'none' };

/**
 * What a COMPLETED row in Team Work offers about Reopen:
 *   reopen   : an ordinary completed task, to an Authorized_Overseer (the existing control and its confirmation);
 *   governed : a source-governed completed task, to an Authorized_Overseer: the explanation, never a working Reopen (the server rejects it);
 *   none     : anything else (an open row, a cancelled task, a restricted one the viewer cannot open, a Caller who oversees nothing).
 */
export function reopenOffer(me: ActionMe | null | undefined, row: ActionRow | null | undefined): ReopenOffer {
  if (row === null || row === undefined || row.status !== 'completed' || row.lifecycle === 'cancelled' || row.redacted === true) return { kind: 'none' };
  if (!isOverseer(me)) return { kind: 'none' };
  if (row.sourceGoverned === true) return { kind: 'governed', text: GOVERNED_REOPEN_TEXT };
  return offeredControls(me, row, 'oversight').controls.includes('reopen') ? { kind: 'reopen' } : { kind: 'none' };
}

// ───────────────────────── the completed list ─────────────────────────

export const COMPLETED_UNAVAILABLE_TEXT = 'Completed team work is not available on this server yet.';
export const COMPLETED_EMPTY_TEXT = 'No completed team work to show.';

/** What the completed list shows (idle, loading, unavailable, error, empty or data with its paging facts). */
export function describeCompleted<T>(state: PagedState<T>): PagedView<T> {
  return describePaged(state, { label: 'completed team work', emptyText: COMPLETED_EMPTY_TEXT, unavailableText: COMPLETED_UNAVAILABLE_TEXT });
}

export interface CompletedOversightDeps<T extends { id: string }> {
  getEpoch: () => number;
  /** `workApi.listOversightCompleted`: one page. */
  fetchCompleted: (query: { limit: number; cursor: string | null }) => Promise<{ items: readonly T[]; next?: string }>;
  isUnavailable?: PagedListDeps<T>['isUnavailable'];
}

/**
 * The paged engine of the completed list. It does NOT read again by itself on a Caller change (`reloadOnCallerChange` false): a Caller who is not an
 * Authorized_Overseer must never be sent a request that can only be refused (task 9.39). The view asks (`ensureLoaded`) once the new Caller's summary
 * says the Team Work view is theirs and the Completed filter is chosen.
 */
export function createCompletedOversight<T extends { id: string }>(deps: CompletedOversightDeps<T>): PagedList<T> {
  return createPagedList<T>({
    getEpoch: deps.getEpoch,
    keyOf: (task) => task.id,
    reloadOnCallerChange: false,
    ...(deps.isUnavailable !== undefined ? { isUnavailable: deps.isUnavailable } : {}),
    fetchPage: async (cursor) => {
      const page = await deps.fetchCompleted({ limit: COMPLETED_PAGE_SIZE, cursor });
      return { items: page.items, next: typeof page.next === 'string' && page.next !== '' ? page.next : null };
    },
  });
}
